import { useCallback, useEffect, useState } from 'react';
import { Client } from 'types/clients';
import { deleteClient, getClients, updateClient, archiveClient } from 'services/clients';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useUpdateSearchParamUrl } from 'hooks/hook';
import { SIZE_CLIENTS_PAGE } from 'const/paging';
import { isNil, omitBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useFlash } from 'contexts/flash';
import { FormCreateClient } from './create/hook';

export const HEADER_CLIENTS = [
  { label: 'Client Name', sort: 'clientName' },
  { label: 'Client Email', sort: 'clientEmail' },
  { label: 'Contact Person', sort: 'contactPerson' },
  { label: 'Actions', width: 400 }
];

type ClientActionType = 'archive' | 'active' | 'delete';
type DeleteStep = 'warning1' | 'warning2';

export const useInjection = () => {
  const navigate = useNavigate();
  const { setFlash } = useFlash();
  const [clients, setClients] = useState<Client[]>([]);
  const [searchParams, setSearchParams] = useUpdateSearchParamUrl();
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [tab, setTab] = useState<'active' | 'archived'>('active');

  const [clientAction, setClientAction] = useState<ClientActionType | undefined>();
  const [clientTarget, setClientTarget] = useState<Client | undefined>();
  const [clientEdit, setClientEdit] = useState<Client | undefined>();
  const [deleteStep, setDeleteStep] = useState<DeleteStep | null>(null);

  const handleGetInitClients = useCallback(async () => {
    const params = {
      size: SIZE_CLIENTS_PAGE,
      page: 1,
      keyword: searchParams.get('searchText') || undefined,
      orderBy: searchParams.get('sortBy') || undefined,
      order: searchParams.get('order') || undefined,
      type: searchParams.get('type') || undefined,
      isArchived: tab === 'archived'
    };
    const data = await getClients(params);
    setClients(data.clients);
    const element = document.getElementById('id-table-clients');
    if (element) {
      element.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (data.total > SIZE_CLIENTS_PAGE) {
      setPage(2);
      setHasMore(true);
      return;
    }
    setHasMore(false);
  }, [searchParams, tab]);

  const fetchMoreData = useCallback(async () => {
    const params = {
      size: SIZE_CLIENTS_PAGE,
      page: page,
      keyword: searchParams.get('searchText') || undefined,
      orderBy: searchParams.get('sortBy') || undefined,
      order: searchParams.get('order') || undefined,
      type: searchParams.get('type') || undefined,
      isArchived: tab === 'archived'
    };
    const data = await getClients(params);
    setClients((prevState) => [...prevState, ...data.clients]);
    if (data.total > SIZE_CLIENTS_PAGE * page) {
      setPage((prevState) => prevState + 1);
      setHasMore(true);
      return;
    }
    setHasMore(false);
  }, [page, searchParams, tab]);
  const resetDeleteFlow = useCallback(() => {
    setClientTarget(undefined);
    setClientAction(undefined);
    setDeleteStep(null);
  }, []);

  const { asyncCallback: asyncGetInitClients } = useAsyncCallback(handleGetInitClients, []);

  const handleSearch = useCallback(
    (searchText: string) => {
      setSearchParams(omitBy({ searchText }, isNil) as { [key: string]: string });
    },
    [setSearchParams]
  );

  const filterByTypeAccess = useCallback(
    (type: string) => {
      setSearchParams(omitBy({ type }, isNil) as { [key: string]: string });
    },
    [setSearchParams]
  );

  useEffect(() => {
    asyncGetInitClients();
  }, [asyncGetInitClients]);

  const onSortHeader = useCallback(
    (sortField: string) => {
      let order = 'asc';
      const sortFieldOld = searchParams.get('sortBy');
      const orderOld = searchParams.get('order');
      if (sortFieldOld === sortField) {
        if (orderOld === 'asc') {
          order = 'desc';
        } else {
          order = 'asc';
        }
      }
      setSearchParams(
        omitBy({ sortBy: sortField, order: order }, isNil) as { [key: string]: string }
      );
    },
    [searchParams, setSearchParams]
  );

  const handleDeleteClient = useCallback(async () => {
    if (!clientTarget?.id || !clientAction) return;

    if (clientAction === 'archive') {
      await archiveClient(clientTarget.id, { isArchive: true });
      setClients((prev) => prev.filter((c) => c.id !== clientTarget.id));
      setFlash({ type: 'success', message: 'Archive client successful' });
      resetDeleteFlow();
      return;
    }

    if (clientAction === 'active') {
      await archiveClient(clientTarget.id, { isArchive: false });
      setClients((prev) => prev.filter((c) => c.id !== clientTarget.id));
      setFlash({ type: 'success', message: 'Active client successful' });
      resetDeleteFlow();
      return;
    }

    if (clientAction === 'delete') {
      if (deleteStep === 'warning1') {
        setDeleteStep('warning2');
        return;
      }

      if (deleteStep === 'warning2') {
        await deleteClient(clientTarget.id);
        setClients((prev) => prev.filter((c) => c.id !== clientTarget.id));
        setFlash({ type: 'success', message: 'Delete client successful' });
        resetDeleteFlow();
      }
    }
  }, [clientTarget, clientAction, deleteStep, setFlash, resetDeleteFlow]);
  const { asyncCallback: asyncHandleDeleteClient } = useAsyncCallback(handleDeleteClient, []);

  const onClickAction = useCallback(
    (href: string) => {
      navigate(href);
    },
    [navigate]
  );

  const handleArchiveClient = useCallback((client: Client) => {
    setClientTarget(client);
    setClientAction('archive');
  }, []);

  const handleActiveClient = useCallback((client: Client) => {
    setClientTarget(client);
    setClientAction('active');
  }, []);

  const handleEditClient = useCallback((client: Client) => {
    setClientEdit(client);
  }, []);

  const handleSubmitEditClient = useCallback(
    async (value: FormCreateClient) => {
      if (!clientEdit?.id) return;
      await updateClient(clientEdit.id.toString(), value);
      setClients((prev) => prev.map((c) => (c.id === clientEdit.id ? { ...c, ...value } : c)));
      setFlash({ type: 'success', message: 'Update client successful' });
      setClientEdit(undefined);
    },
    [clientEdit?.id, setFlash]
  );

  const { asyncCallback: asyncSubmitEditClient } = useAsyncCallback(handleSubmitEditClient, []);

  const handleDeleteClientClick = useCallback((client: Client) => {
    setClientTarget(client);
    setClientAction('delete');
    setDeleteStep('warning1');
  }, []);

  return {
    clients,
    setClients,
    fetchMoreData,
    hasMore,
    handleSearch,
    onSortHeader,
    filterByTypeAccess,
    searchParams,
    navigateToCreateCline: () => navigate('/clients/create'),
    onClickAction,
    clientTarget,
    clientAction,
    setClientTarget,
    setClientAction,
    handleDeleteClientClick,
    handleDelete: asyncHandleDeleteClient,
    tab,
    setTab,
    handleArchiveClient,
    handleActiveClient,
    clientEdit,
    setClientEdit,
    handleEditClient,
    handleSubmitEditClient: asyncSubmitEditClient,
    deleteStep,
    resetDeleteFlow
  };
};
